/**
 * Mirrors backend DTOs in `api/BtScheduler.Api/DTOs/ScheduleDtos.cs`.
 * Guids and ISO timestamps are kept as `string` and parsed on demand.
 */

export type CalendarViewMode = "week" | "day";

export interface ScheduleIssueCounts {
  errors: number;
  warnings: number;
}

export interface ScheduleCounts {
  courseEvents: number;
  ignoredEvents: number;
  distinctCourseIds: number;
  distinctRooms: number;
  distinctTeachers: number;
  distinctSubjectAreas: number;
  /**
   * Number of {@link Room} rows newly inserted by auto-discovery on the
   * just-completed create or refresh. Zero on a refresh where every event
   * Location matched an existing room.
   */
  roomsDiscovered: number;
}

export interface ScheduleListItem {
  id: string;
  name: string;
  createdAt: string;
  lastImportedAt: string | null;
  eventCount: number;
  issueCounts: ScheduleIssueCounts;
}

export interface Schedule {
  id: string;
  name: string;
  outlookUrl: string;
  timeZone: string;
  createdAt: string;
  lastImportedAt: string | null;
  eventCount: number;
  issueCounts: ScheduleIssueCounts;
  /** Populated only on create/refresh responses. */
  counts: ScheduleCounts | null;
}

export interface CreateScheduleRequest {
  name: string;
  outlookUrl: string;
}

/**
 * Mirrors `RoomDto` in `api/BtScheduler.Api/DTOs/RoomDtos.cs`. `type` arrives
 * as the enum name string thanks to the `JsonStringEnumConverter` registered
 * in `Program.cs`.
 */
export const ROOM_TYPES = ["Internal", "External"] as const;
export type RoomType = (typeof ROOM_TYPES)[number];

/** Type guard for narrowing arbitrary strings (e.g. select `value`) to a `RoomType`. */
export function isRoomType(v: string): v is RoomType {
  return (ROOM_TYPES as readonly string[]).includes(v);
}

export interface Room {
  id: string;
  scheduleId: string;
  name: string;
  type: RoomType;
  capacity: number | null;
  building: string | null;
  notes: string | null;
  /**
   * Subject-area IDs (Guid strings) this room is restricted to. Empty array =
   * no restriction (any subject area may be scheduled here). Mutated via the
   * dedicated `PUT .../rooms/{roomId}/allowed-subject-areas` endpoint, not the
   * room create/update payload.
   */
  allowedSubjectAreaIds: string[];
  createdAt: string;
  updatedAt: string;
}

export interface RoomCreateRequest {
  name: string;
  type: RoomType;
  capacity?: number | null;
  building?: string | null;
  notes?: string | null;
}

export type RoomUpdateRequest = RoomCreateRequest;

/**
 * Body for `PUT /api/schedules/{id}/rooms/{roomId}/allowed-subject-areas`.
 * Empty array clears all restrictions.
 */
export interface RoomAllowedSubjectAreasRequest {
  subjectAreaIds: string[];
}

/**
 * Mirrors `SubjectAreaDto` in `api/BtScheduler.Api/DTOs/SubjectAreaDtos.cs`.
 * `colorHex` is a `#RRGGBB` string; the UI palette constrains writes to 6
 * preset swatches but the backend accepts any valid hex.
 */
export interface SubjectArea {
  id: string;
  scheduleId: string;
  name: string;
  colorHex: string;
  displayOrder: number;
  createdAt: string;
  updatedAt: string;
}

export interface SubjectAreaCreateRequest {
  name: string;
  colorHex: string;
  displayOrder?: number;
}

export interface SubjectAreaUpdateRequest {
  name: string;
  colorHex: string;
}

/**
 * Mirrors `CourseRequestDto` in `api/BtScheduler.Api/DTOs/CourseRequestDtos.cs`.
 * Priority arrives as the enum name string (`JsonStringEnumConverter`).
 */
export const REQUEST_PRIORITIES = [
  "Required",
  "High",
  "Normal",
  "Low",
] as const;
export type RequestPriority = (typeof REQUEST_PRIORITIES)[number];

/** Type guard for narrowing arbitrary input to a {@link RequestPriority}. */
export function isRequestPriority(value: unknown): value is RequestPriority {
  return (
    typeof value === "string" &&
    (REQUEST_PRIORITIES as readonly string[]).includes(value)
  );
}

export interface CourseRequest {
  id: string;
  scheduleId: string;
  studentIdentifier: string;
  /**
   * Canonical (trim + lower-invariant) form of {@link studentIdentifier},
   * mirrored from the DTO. The load-bearing join key: the grade calendar filter
   * joins a solver-placed student to their {@link grade} through this value.
   */
  normalizedStudentIdentifier: string;
  courseId: string;
  priority: RequestPriority;
  /** Student grade level (e.g. 9–12), or null when the roster carried none. */
  grade: number | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * Mirrors `CourseRequestImportResultDto`. Returned by the import endpoint
 * after a successful (or partially successful) parse.
 */
export interface CourseRequestImportResult {
  totalRowsInFile: number;
  importedRows: number;
  droppedRows: number;
  distinctStudents: number;
  parserIssueCodes: string[];
}

/**
 * Mirrors `ScheduleValidationIssueDto`. Stage / severity arrive as enum-name
 * strings. `eventId` is null when the issue is not tied to a specific event
 * (e.g. course-requests import issues).
 */
export const ISSUE_STAGES = [
  "CalendarImport",
  "CourseRequestsImport",
  "PostRun",
] as const;
export type IssueStage = (typeof ISSUE_STAGES)[number];

export const ISSUE_SEVERITIES = ["Error", "Warning"] as const;
export type IssueSeverity = (typeof ISSUE_SEVERITIES)[number];

export interface ScheduleValidationIssue {
  id: string;
  eventId: string | null;
  /**
   * Run that produced this issue. Null on Stage 1 / Stage 2 issues (which
   * carry no run context); the current run id on Stage 3 PostRun issues.
   */
  runId: string | null;
  stage: IssueStage;
  severity: IssueSeverity;
  code: string;
  message: string;
  /**
   * Typed context columns added in Phase 4 Slice 1 to power grouped
   * presentation on the Validation Issues tab without parsing `message`.
   * Required + nullable on the wire (matches the `eventId` convention):
   * Stage 1/2 issues leave them null; Stage 3 PostRun issues populate the
   * columns that apply to their rule.
   */
  normalizedStudentIdentifier: string | null;
  studentIdentifier: string | null;
  /** Friendly student name from the course-request CSV; null when unknown. */
  studentName: string | null;
  courseId: string | null;
  /** Friendly course title (CSV name, else calendar event summary); null when unknown. */
  courseName: string | null;
  sectionLabel: string | null;
  teacherEmail: string | null;
  /**
   * Students behind an aggregated issue (e.g. a "could not place" course whose
   * message only carries a count), recomputed server-side. The API always sends
   * this (empty for issues without a roster); optional here so older fixtures /
   * payloads without the field still type-check. Powers the expandable detail.
   */
  affectedStudents?: AffectedStudent[];
  /**
   * Calendar events behind a per-day issue (e.g. the classes in a teacher's
   * overloaded day), recomputed server-side with pre-formatted time/duration
   * labels. Always sent by the API (empty when the issue has no event
   * breakdown); optional here for the same fixture-compatibility reason as
   * {@link affectedStudents}. Powers the expandable detail's event list.
   */
  events?: IssueEvent[];
}

/** One student behind an aggregated issue, with a short best-effort reason. */
export interface AffectedStudent {
  studentIdentifier: string;
  studentName: string | null;
  /** e.g. "Conflicts with ENG200 (A) at Mon 13:00", "No open seat", or null. */
  detail: string | null;
}

/**
 * One calendar event behind a per-day issue (e.g. a class in a teacher's
 * overloaded day). Mirrors `IssueEventDto`; `timeLabel` ("Mon 13:00–14:00")
 * and `durationLabel` ("1h 30m") are pre-formatted server-side in the schedule
 * timezone. Consumed by the issue detail expander's event list.
 */
export interface IssueEvent {
  courseId: string;
  courseName: string | null;
  sectionLabel: string | null;
  timeLabel: string;
  durationLabel: string;
}

/**
 * Mirrors `CalendarEventDto` in `api/BtScheduler.Api/DTOs/ScheduleDtos.cs`.
 * `dtStartUtc` / `dtEndUtc` are UTC ISO 8601 strings (with `Z` suffix on the wire).
 */
export interface CalendarEvent {
  id: string;
  uid: string;
  summary: string;
  dtStartUtc: string;
  dtEndUtc: string;
  allDay: boolean;
  location: string | null;
  courseId: string | null;
  /** Comma-separated additional course codes this one section also serves (see backend `CalendarEvent.AdditionalCourseIds`). */
  additionalCourseIds: string | null;
  teacherEmail: string | null;
  subjectArea: string | null;
  capacity: number | null;
  section: string | null;
  linkedCourses: string | null;
  rawDescription: string | null;
  isOverrideInstance: boolean;
  recurrenceIdUtc: string | null;
}

/**
 * Mirrors backend `RunStatus` enum (serialized via `JsonStringEnumConverter`
 * → PascalCase strings on the wire). Use the `as const` tuple form so we keep
 * both the literal-union TS type and a runtime list of values for exhaustive
 * checks / dropdowns. Pattern matches `ROOM_TYPES` / `REQUEST_PRIORITIES`.
 */
export const RUN_STATUSES = [
  "Pending",
  "Running",
  "Succeeded",
  "Failed",
] as const;
export type RunStatus = (typeof RUN_STATUSES)[number];

/**
 * Mirrors backend `AssignmentSource`. `Override` is reserved for Phase 5
 * manual reassignment; Phase 4 only produces `Computed` rows.
 */
export const ASSIGNMENT_SOURCES = ["Computed", "Override"] as const;
export type AssignmentSource = (typeof ASSIGNMENT_SOURCES)[number];

/**
 * Mirrors `AssignmentDto`. One per (student, section) placement produced by
 * a scheduler run. `normalizedStudentIdentifier` and `normalizedCourseId` are
 * lower-cased trimmed forms suitable for joins; the display fields preserve
 * the original casing as it appeared on the request / event.
 */
export interface Assignment {
  id: string;
  runId: string;
  studentIdentifier: string;
  normalizedStudentIdentifier: string;
  /** Friendly student name from the course-request CSV; null when unknown. */
  studentName: string | null;
  courseId: string;
  normalizedCourseId: string;
  sectionLabel: string | null;
  source: AssignmentSource;
}

/**
 * Mirrors `RunDto`. Full run payload including the assignment list. Returned
 * by POST `/runs` (the creating call) and GET `/runs/{runId}` (single fetch).
 * The hashes + counts are Phase 4's lightweight "did inputs change?" snapshot;
 * the full input JSON snapshot is deferred to Phase 5.
 */
export interface Run {
  id: string;
  scheduleId: string;
  runByUserId: string;
  status: RunStatus;
  startedAt: string;
  completedAt: string | null;
  eventsInputHash: string;
  requestsInputHash: string;
  eventsInputCount: number;
  requestsInputCount: number;
  assignmentCount: number;
  errorIssueCount: number;
  warningIssueCount: number;
  failureReason: string | null;
  assignments: Assignment[];
  /**
   * Live completion percentage (0–100) while the run is executing; null once it
   * reaches a terminal status. Sourced from the solver's real objective/bound
   * gap — a true progress signal, not a wall-clock estimate.
   */
  progressPercent: number | null;
}

/**
 * Mirrors `RunSummaryDto`. Lightweight projection returned by GET `/runs`
 * (the list endpoint) — omits the assignments collection so the runs list
 * page can stay snappy.
 *
 * `runByUserDisplayName` is required + nullable on the wire (matches the
 * Phase 4 P2 convention; see `docs/solutions/integration-issues/
 * dto-context-fields-required-nullable-not-optional.md`). The backend coerces
 * whitespace-only display names to null; the FE renders "Unknown user" in
 * that case.
 */
export interface RunSummary {
  id: string;
  scheduleId: string;
  runByUserId: string;
  runByUserDisplayName: string | null;
  status: RunStatus;
  startedAt: string;
  completedAt: string | null;
  assignmentCount: number;
  errorIssueCount: number;
  warningIssueCount: number;
  /**
   * Live completion percentage (0–100) while the run is executing; null once it
   * reaches a terminal status. Drives the run button's true progress readout.
   */
  progressPercent: number | null;
}

/**
 * Mirrors `SectionDto` on the wire. A section is derived from a grouping of
 * {@link CalendarEvent} rows by `(NormalizedCourseId, SectionLabel ?? "")`;
 * there is no Section table. {@link assignedCount} is `null` until at least
 * one Succeeded run has been recorded for the parent schedule — afterwards
 * it carries the count from the latest succeeded run (zero when nothing was
 * placed in the section).
 *
 * Earliest / latest timestamps are UTC ISO 8601 strings; the Sections tab
 * formats them in the schedule's local timezone for display.
 */
export interface Section {
  courseId: string;
  /** Friendly course title from the first distinct event summary; null when unknown. */
  courseName: string | null;
  /**
   * Every display course code this one physical section serves (primary
   * first). More than one when the event's `Course ID:` line lists several
   * codes; students requesting any of them share this section.
   */
  acceptedCourseIds: readonly string[];
  /** Normalized forms of {@link acceptedCourseIds}; used to match assignments to this section. */
  acceptedNormalizedCourseIds: readonly string[];
  normalizedCourseId: string;
  sectionLabel: string | null;
  teacherEmail: string | null;
  roomName: string | null;
  earliestStartUtc: string | null;
  latestEndUtc: string | null;
  capacity: number | null;
  assignedCount: number | null;
}

/**
 * Mirrors backend `ReassignFitStatus` enum (serialized as PascalCase strings
 * via `JsonStringEnumConverter`). Drives badge color + warning rendering on
 * the reassign modal. `Fits` = green; `TimeConflict` = red; `AtCapacity` =
 * amber. Pattern follows {@link RUN_STATUSES} / {@link ROOM_TYPES}.
 */
export const REASSIGN_FIT_STATUSES = [
  "Fits",
  "TimeConflict",
  "AtCapacity",
] as const;
export type ReassignFitStatus = (typeof REASSIGN_FIT_STATUSES)[number];

/** Type guard for narrowing arbitrary wire input to a {@link ReassignFitStatus}. */
export function isReassignFitStatus(
  value: unknown,
): value is ReassignFitStatus {
  return (
    typeof value === "string" &&
    (REASSIGN_FIT_STATUSES as readonly string[]).includes(value)
  );
}

/**
 * Mirrors `ReassignOptionDto`. One candidate section that the modal can
 * surface for a student. Nullable strings (`sectionLabel`, `teacherDisplayName`,
 * `roomName`, `warning`) are required + nullable on the wire — never optional
 * + nullable (matches the Phase 4 P2 convention; see
 * `docs/solutions/integration-issues/dto-context-fields-required-nullable-not-optional.md`).
 *
 * `isCurrent` is `true` when this candidate matches the student's current
 * assignment for the course; the modal renders a "Current" pill instead of a
 * fit-status badge for that row.
 */
export interface ReassignOption {
  courseId: string;
  /** Friendly course title from the section's first distinct event summary; null when unknown. */
  courseName: string | null;
  sectionLabel: string | null;
  teacherDisplayName: string | null;
  roomName: string | null;
  timeLabel: string;
  fitStatus: ReassignFitStatus;
  warning: string | null;
  isCurrent: boolean;
}

/**
 * Body for `POST /api/schedules/{scheduleId}/runs/{runId}/assignments`.
 * `sectionLabel` is required + nullable so callers explicitly opt in to the
 * label-less case (which back-resolves to the section with no label on the
 * server).
 */
export interface ApplyOverrideRequest {
  studentIdentifier: string;
  courseId: string;
  sectionLabel: string | null;
}

/**
 * Body for the "accept small overlaps" force-place action. Mirrors
 * `ForcePlaceRequest`. Post with `dryRun: true` to preview, `false` to apply.
 */
export interface ForcePlaceRequest {
  toleranceMinutes: number;
  dryRun: boolean;
}

/** Mirrors `ForcePlacementDto` — one (student → section) placement. */
export interface ForcePlacement {
  studentIdentifier: string;
  studentName: string | null;
  courseId: string;
  normalizedCourseId: string;
  sectionLabel: string | null;
  overlapMinutes: number;
}

/** Mirrors `ForcePlaceSkipDto` — a student that could not be placed. */
export interface ForcePlaceSkip {
  studentIdentifier: string;
  studentName: string | null;
  courseId: string;
  sectionLabel: string | null;
  overlapMinutes: number;
  detail: string;
}

/** Mirrors `ForcePlaceResultDto` — the preview / applied outcome. */
export interface ForcePlaceResult {
  runId: string;
  toleranceMinutes: number;
  applied: boolean;
  placed: ForcePlacement[];
  skippedForCapacity: ForcePlaceSkip[];
  skippedForLinkedCourse: ForcePlaceSkip[];
}

/**
 * Phase 6 export types. One per modal card. Pattern follows {@link RUN_STATUSES} /
 * {@link ROOM_TYPES}: tuple-as-const so we keep both the literal-union TS type
 * and a runtime list of values for exhaustive iteration.
 */
export const EXPORT_TYPES = [
  "overall",
  "overall-split",
  "per-teacher",
  "per-student",
  "per-room",
] as const;
export type ExportType = (typeof EXPORT_TYPES)[number];

/** Type guard for narrowing arbitrary wire input to an {@link ExportType}. */
export function isExportType(value: unknown): value is ExportType {
  return (
    typeof value === "string" &&
    (EXPORT_TYPES as readonly string[]).includes(value)
  );
}

/**
 * Mirrors `ExportManifestDto` in the Phase 6 backend. Drives modal card
 * visibility + footer text in a single round-trip on modal open. The
 * `*Enabled` flags reflect the server-side feature-flag state (only
 * `perRoomEnabled` is currently toggleable; the other three are always true
 * but kept on the wire so the contract is symmetric).
 */
export interface ExportManifestDto {
  runNumber: number;
  runStartedAtUtc: string;
  overallEnabled: boolean;
  perTeacherEnabled: boolean;
  perStudentEnabled: boolean;
  perRoomEnabled: boolean;
}
